import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MedicalRecordModal } from "../../../features/ShowAccountSlice";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as Closemyrecord } from "../../../assets/icons/closeModal.svg";
import { ReactComponent as DownloadRecord } from "../../../assets/icons/download.svg";
import FileSaver from "file-saver";
import { ToastContainer, toast } from "react-toastify";

import "../../../styles/ViewRecord.scss";
import pdfFile from "../../../assets/document.pdf";
import {
  DOWNLOAD_MEDIDCAL_REPORT,
  GET_SINGLE_MEDICAL_REPORT,
} from "../../../helper/Url";
import { AXIOS_GET } from "../../../config/axios";
import { selectLanguage } from "../../../features/LanguageSlice";
import Loader from "../../../helper/Loader";

/**
 * Removes backslashes and escaped characters from a raw URL string.
 * Example: https:\/\/example.com\/file.pdf -> https://example.com/file.pdf
 */
const cleanUrl = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== "string") return rawUrl;
  return rawUrl.replace(/\\\//g, "/").replace(/\\/g, "");
};

/**
 * Checks if a string is a valid URL (starts with http:// or https://)
 */
const isUrl = (str) => {
  if (!str || typeof str !== "string") return false;
  return str.trim().startsWith("http://") || str.trim().startsWith("https://");
};

/**
 * Detects file type from URL or filename
 */
const detectFileType = (url) => {
  if (!url || typeof url !== "string") return "pdf";
  
  const urlLower = url.toLowerCase();
  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp", ".svg"];
  const pdfExtensions = [".pdf"];
  
  // Check for image extensions
  if (imageExtensions.some(ext => urlLower.includes(ext))) {
    return "image";
  }
  
  // Check for PDF extensions
  if (pdfExtensions.some(ext => urlLower.includes(ext))) {
    return "pdf";
  }
  
  // Default to PDF for backward compatibility
  return "pdf";
};

/**
 * Fetches file as blob to avoid CORS issues and creates object URL
 */
const fetchFileAsBlob = async (url) => {
  try {
    const cleanedUrl = cleanUrl(url);
    console.log("Fetching file as blob from:", cleanedUrl);
    
    const response = await fetch(cleanedUrl, {
      method: "GET",
      mode: "cors",
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const blob = await response.blob();
    const detectedType = detectFileType(cleanedUrl);
    const objectUrl = URL.createObjectURL(blob);
    
    console.log("File fetched successfully, type:", detectedType);
    return { blob, objectUrl, fileType: detectedType };
  } catch (error) {
    console.error("Error fetching file as blob:", error);
    throw error;
  }
};

const ViewRecord = ({ show, Data }) => {
  const dispatch = useDispatch();
  const languageValue = useSelector(selectLanguage);
  const [btnDowload, setbtnDowload] = useState(false);
  const [showDownload, setshowDownload] = useState(false);
  const [displayLoader, setdisplayLoader] = useState({
    onsuccess: true,
    onprocessing: true,
  });
  const token = JSON.parse(localStorage.getItem("token"))?.access_token;
  const [fileUrl, setfileUrl] = useState(pdfFile);
  const [directFileUrl, setDirectFileUrl] = useState(null);
  const [fileType, setFileType] = useState("pdf"); // 'pdf', 'image', or 'other'
  const [previewFailed, setPreviewFailed] = useState(false);
  const blobUrlRef = useRef(null);
  const retryCountRef = useRef(2);
  const getSinglePetFileRef = useRef();
  const lastFetchedMedIdRef = useRef(null);
  const wasOpenRef = useRef(false);

  const downloadSinglePetFile = (
    path = null,
    fileName = null,
    directUrl = null
  ) => {
    // Determine file name from URL or use default
    let downloadFileName = fileName;
    if (!downloadFileName && directUrl) {
      try {
        const url = new URL(cleanUrl(directUrl));
        const pathname = url.pathname;
        const urlFileName = pathname.split("/").pop() || "medical_record";
        downloadFileName = urlFileName.includes(".") ? urlFileName : `${urlFileName}.pdf`;
      } catch (e) {
        downloadFileName = "medical_record.pdf";
      }
    }
    downloadFileName = downloadFileName || "medical_record.pdf";
    setbtnDowload(true);
    
    // If direct URL is provided, use it directly
    if (directUrl) {
      const cleanedUrl = cleanUrl(directUrl);
      console.log("Download - Original URL:", directUrl);
      console.log("Download - Cleaned URL:", cleanedUrl);
      // S3 signed URLs don't need Authorization header
      fetch(cleanedUrl, {
        method: "GET",
      })
        .then((response) => response.blob())
        .then((blob) => {
          setbtnDowload(false);
          FileSaver.saveAs(blob, downloadFileName);
        })
        .catch((error) => {
          setbtnDowload(false);
          languageValue === "En"
            ? toast("Failed to download file")
            : toast("Échec du téléchargement du fichier");
        });
    } else {
      // Fallback to original API endpoint
    fetch(DOWNLOAD_MEDIDCAL_REPORT + path, {
      method: "GET",
      headers: { Authorization: "Bearer " + token },
    })
      .then((response) => response.blob())
      .then((blob) => {
        setbtnDowload(false);
          FileSaver.saveAs(blob, downloadFileName);
        })
        .catch((error) => {
          setbtnDowload(false);
          languageValue === "En"
            ? toast("Failed to download file")
            : toast("Échec du téléchargement du fichier");
      });
    }
  };

  const getSinglePetFile = useCallback(() => {
    setfileUrl(pdfFile);
    setDirectFileUrl(null);
    setshowDownload(false);
    setFileType("pdf");
    setPreviewFailed(false);
    // Clean up previous blob URL if exists
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    retryCountRef.current = 2; // Reset retry count
    
    AXIOS_GET(`${GET_SINGLE_MEDICAL_REPORT}${Data?.med_id}`, token)
      .then((res) => {
        console.log("API Response:", res.data);
        console.log("Response status:", res.data.status);
        console.log("Response data:", res.data.data);
        
        if ((res.data.status === 200 || res.data.status === "200") && res.data.data.download_url) {
          const urlData = res.data.data.download_url;
          
          // Always clean the URL first
          const cleanedUrl = cleanUrl(urlData);
          
          // Check if it's a URL string or base64
          if (isUrl(cleanedUrl)) {
            // It's a URL - use cleaned URL and fetch as blob to avoid CORS
            setDirectFileUrl(cleanedUrl);
            setdisplayLoader({ onsuccess: true, onprocessing: true });
            
            // Fetch file as blob to avoid CORS issues
            fetchFileAsBlob(cleanedUrl)
              .then(({ blob, objectUrl, fileType: detectedType }) => {
                // Clean up previous blob URL if exists
                if (blobUrlRef.current) {
                  URL.revokeObjectURL(blobUrlRef.current);
                }
                
                blobUrlRef.current = objectUrl;
                setFileType(detectedType);
                setfileUrl(objectUrl);
                setshowDownload(true);
                setdisplayLoader({ onsuccess: true, onprocessing: false });
              })
              .catch((error) => {
                console.error("Failed to fetch file:", error);
                setdisplayLoader({ onsuccess: false, onprocessing: false });
                setPreviewFailed(true);
                setshowDownload(true); // Show download button even on error
                languageValue === "En"
                  ? toast("Failed to load file preview. You can open it in a new tab.")
                  : toast("Échec du chargement de l'aperçu. Vous pouvez l'ouvrir dans un nouvel onglet.");
              });
          } else {
            // It's base64 - decode and process
            setDirectFileUrl(null);
            
          // Function to convert Base64 to Uint8Array
          function base64ToUint8Array(base64) {
            const binaryString = window.atob(base64);
            const len = binaryString.length;
            const bytes = new Uint8Array(len);
            for (let i = 0; i < len; ++i) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            return bytes;
          }

          // Function to convert Uint8Array to Blob
            function uint8ArrayToBlob(uint8Array, mimeType = "application/pdf") {
              return new Blob([uint8Array], { type: mimeType });
          }

            // Base64 encoded string representing the file content
            const base64String = cleanedUrl;
            
            // Try to detect MIME type from base64 data URL if present
            let mimeType = "application/pdf";
            if (base64String.startsWith("data:")) {
              const mimeMatch = base64String.match(/data:([^;]+);/);
              if (mimeMatch) {
                mimeType = mimeMatch[1];
              }
            }

          // Convert Base64 to Uint8Array
            const base64Data = base64String.includes(",") 
              ? base64String.split(",")[1] 
              : base64String;
            const uint8Array = base64ToUint8Array(base64Data);

          // Convert Uint8Array to Blob
            const blob = uint8ArrayToBlob(uint8Array, mimeType);
            
            // Detect file type
            const detectedType = mimeType.startsWith("image/") ? "image" : "pdf";
            setFileType(detectedType);

          // Create object URL from Blob
            const objectUrl = URL.createObjectURL(blob);
            
            // Clean up previous blob URL if exists
            if (blobUrlRef.current) {
              URL.revokeObjectURL(blobUrlRef.current);
            }
            blobUrlRef.current = objectUrl;
          setshowDownload(true);
            setdisplayLoader({ onsuccess: true, onprocessing: false });
            setfileUrl(objectUrl);
          }
        } else {
          if (show) {
            languageValue === "En"
              ? toast("No file associated with this pet")
              : toast("Aucun fichier associé à cet animal");
            setdisplayLoader({ onsuccess: false, onprocessing: false });
          }
        }
      })
      .catch((e) => {
        console.error("Error fetching medical record:", e);
        if (retryCountRef.current > 0) {
          retryCountRef.current = retryCountRef.current - 1;
          setTimeout(() => {
            getSinglePetFileRef.current?.();
          }, 100);
        } else {
          setdisplayLoader({ onsuccess: false, onprocessing: false });
          if (show) {
            languageValue === "En"
              ? toast("Failed to load medical record")
              : toast("Échec du chargement du dossier médical");
          }
        }
      });
  }, [Data, token, show, languageValue]);

  // Store the latest function in ref
  getSinglePetFileRef.current = getSinglePetFile;

  // Always send request when modal opens (show becomes true)
  useEffect(() => {
    // Check if modal just opened (was closed, now open)
    const justOpened = show && !wasOpenRef.current;
    
    if (show && Data?.med_id && justOpened) {
      // Always fetch when modal opens, even if it's the same record
      console.log("Modal opened, fetching fresh medical record for:", Data?.med_id);
      lastFetchedMedIdRef.current = Data.med_id;
    getSinglePetFile();
    }
    
    // Update the ref to track modal state
    wasOpenRef.current = show;
  }, [show, Data?.med_id, getSinglePetFile]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, []);

  return (
    <>
      <ToastContainer />
      <AnimatePresence>
      {show && (
        <motion.div
            key="medical-record-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="medical-modal">
            <div className="record-view-table">
              <div className="header_intro">
                <h1>
                  {Data?.medical_record_title
                    ? Data?.medical_record_title
                    : "N/A"}
                </h1>
                <Closemyrecord onClick={() => dispatch(MedicalRecordModal())} />
              </div>

              <div className="pdf_action">
                <div className="download_div">
                  {showDownload && (
                    <>
                      {btnDowload ? (
                        <div className="download_div">
                          <button>
                            <Loader></Loader>
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() =>
                            downloadSinglePetFile(
                              Data?.med_id,
                              null,
                              directFileUrl
                            )
                          }
                        >
                          <DownloadRecord />
                          {languageValue === "En" ? "Download" : "Télécharger"}
                        </button>
                      )}
                    </>
                  )}
                </div>
                {showDownload ? (
                  <div className="pdf_container">
                    {previewFailed ? (
                      // Show open in new tab option when preview fails
                      <div style={{ 
                        display: "flex", 
                        flexDirection: "column",
                        justifyContent: "center", 
                        alignItems: "center",
                        width: "100%",
                        height: "100%",
                        padding: "20px",
                        gap: "20px"
                      }}>
                        <p style={{ 
                          color: "#666",
                          textAlign: "center",
                          margin: "0"
                        }}>
                          {languageValue === "En" 
                            ? "Preview failed. Click below to open in a new tab." 
                            : "L'aperçu a échoué. Cliquez ci-dessous pour ouvrir dans un nouvel onglet."}
                        </p>
                        <a 
                          href={directFileUrl || fileUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ 
                            padding: "12px 24px",
                            backgroundColor: "#007bff",
                            color: "white",
                            textDecoration: "none",
                            borderRadius: "4px",
                            fontSize: "16px",
                            fontWeight: "500"
                          }}
                        >
                          {languageValue === "En" ? "Open in New Tab" : "Ouvrir dans un nouvel onglet"}
                        </a>
                      </div>
                    ) : fileType === "pdf" ? (
                      <div style={{ 
                        display: "flex", 
                        justifyContent: "center", 
                        alignItems: "center",
                        width: "100%",
                        height: "100%",
                        padding: "20px"
                      }}>
                        <iframe
                          src={fileUrl}
                          title="PDF Preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            border: "none",
                            minHeight: "600px"
                          }}
                          onError={(e) => {
                            console.error("Error loading PDF:", e);
                            setPreviewFailed(true);
                            languageValue === "En"
                              ? toast("Failed to load PDF preview. Opening in new tab...")
                              : toast("Échec du chargement de l'aperçu PDF. Ouverture dans un nouvel onglet...");
                            // Open in new tab automatically
                            if (directFileUrl || fileUrl) {
                              setTimeout(() => {
                                window.open(directFileUrl || fileUrl, "_blank");
                              }, 500);
                            }
                          }}
                        />
                      </div>
                    ) : fileType === "image" ? (
                      <div style={{ 
                        display: "flex", 
                        justifyContent: "center", 
                        alignItems: "center",
                        width: "100%",
                        height: "100%",
                        padding: "20px"
                      }}>
                        <img 
                          src={fileUrl} 
                          alt="Medical record" 
                          style={{ 
                            maxWidth: "100%", 
                            maxHeight: "100%",
                            objectFit: "contain"
                          }} 
                          onError={(e) => {
                            console.error("Error loading image:", e);
                            setPreviewFailed(true);
                            languageValue === "En"
                              ? toast("Failed to load image preview. Opening in new tab...")
                              : toast("Échec du chargement de l'aperçu d'image. Ouverture dans un nouvel onglet...");
                            // Open in new tab automatically
                            if (directFileUrl || fileUrl) {
                              setTimeout(() => {
                                window.open(directFileUrl || fileUrl, "_blank");
                              }, 500);
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <div style={{ 
                        display: "flex", 
                        justifyContent: "center", 
                        alignItems: "center",
                        width: "100%",
                        height: "100%",
                        padding: "20px"
                      }}>
                        <a 
                          href={fileUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ 
                            padding: "10px 20px",
                            backgroundColor: "#007bff",
                            color: "white",
                            textDecoration: "none",
                            borderRadius: "4px"
                          }}
                        >
                          {languageValue === "En" ? "Open File" : "Ouvrir le fichier"}
                        </a>
                      </div>
                    )}
                  </div>
                ) : displayLoader.onsuccess ? (
                  <div className="download_div">
                    <button>
                      <Loader></Loader>
                    </button>
                  </div>
                ) : languageValue === "En" ? (
                  "No file associated with this pet"
                ) : (
                  "Aucun fichier associé à cet animal"
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};

export default ViewRecord;
